import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useHypeU, courseOf, loadCourses, classCtl, keepSeat } from '../../city/hypeu';
import { api } from '../../services/api';
import { API_BASE, AUTH_TOKEN } from '../../config';
import { audioEngine } from '../../services/audioEngine';
import { takeAudio, releaseAudio, onAudio } from '../../services/audioPool';
import { useNarrow } from '../SceneChat';
import { ChatAcceptance, useChatAcceptance } from '../ChatAcceptance';
import { ClassControls } from './Classroom';
import useAvatarVoiceChat from '../../hooks/useAvatarVoiceChat';

// THE CLASS (Hype-U). Khari: "We will take a seat in front of them and the
// course can start." Sit in a classroom (city/hypeu: one room per course) and
// its host teaches the lesson — the learn page's own: their words, page by page
// (GET /api/learn `voice.pages`), each read in their voice (POST
// /api/learn/listen → { url, seconds }, the recording the website plays), the
// next page after a breath. Stand up and it stops; sit down again (same visit)
// and it picks up where it was. The music dips for the class.
//
// WHERE IT SHOWS (2026-11-01, Khari: "the content is sitting over the
// instructor … make the whiteboard taller so the content can fit there instead
// of the floating modal"): this writes the class into useHypeU `lesson` and the
// BOARD draws it, controls and all (components/hypeu Classroom). On a phone the
// board is a sliver, so the page reads in a sheet at the bottom instead.
//
// ASKING (Khari: "be able to ask questions at the end of each page … Allow for a
// question or short chat after"): the website's /learn asking, unchanged — the
// ordinary chat (`surface: 'learn'`, so it lands in the one thread you have with
// that artist), the lesson's suggested questions answered from their
// recordings (POST /api/learn/answer), and a typed question's answer read aloud
// live (POST /api/learn/speak, members, the course's live-voice minutes). The
// reading pauses while you ask; "Back to the lesson" picks it up. A guest is
// shown the way to join (a new tab — an engaged guest is never sent away).
//
// THE SEAT (Khari: "When the lesson ended, I was ejected from my seat … Eject
// after a period of inactivity."): it was the shared room's two-minute hold
// running out under a longer class. A classroom's chairs are kept while you are
// busy (rooms.js `seatKeepAlive`, city/hypeu keepSeat): while the reading
// plays, while an answer is coming or being spoken, and on every button and
// keystroke. Two quiet minutes after that, you are stood up.
//
// ⚠ A stitched reading often never fires `ended` (the website found it): a page
// is over when playback reaches its measured `seconds`, or `ended`, whichever
// comes first.
const GOLD = '#d8a640';
const BREATH_MS = 1600;
const ASK_TIMEOUT_MS = 45000;
const PLANS = 'https://www.hypetunes.com/plans';
// THEIR AGENT ANSWERS (2026-11-04): the call closes this long after the last
// answer is said, and a question it has not answered in this long goes the written way.
const AGENT_IDLE_MS = 2 * 60000;
const AGENT_REPLY_MS = 30000;

const authed = () => ({ Authorization: `Bearer ${AUTH_TOKEN}`, 'Content-Type': 'application/json' });
async function postJson(path, body, ms = ASK_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const r = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: authed(), body: JSON.stringify(body), signal: ctrl.signal });
    const d = await r.json().catch(() => ({}));
    return { r, d };
  } finally { clearTimeout(t); }
}

const setQa = (patch) => useHypeU.setState((s) => ({ qa: patch === null ? null : { ...(s.qa || {}), ...patch } }));

export default function CourseRunner() {
  const interior = useWorldStore((s) => s.interior);
  const seatedHere = useWorldStore((s) => s.seated?.phase === 'seated');
  const course = courseOf(interior);
  const courses = useHypeU((s) => s.courses);
  const qa = useHypeU((s) => s.qa);
  const narrow = useNarrow();
  const terms = useChatAcceptance();
  const active = !!course && seatedHere;

  const full = course ? (courses || []).find((c) => c.slug === course.slug) || null : null;
  const lesson = full ? (full.lessons || []).find((l) => l.slug === course.lesson) || full.lessons?.[0] || null : null;
  const voice = lesson?.voice || null;
  const pages = voice?.pages || [];
  const host = full?.host?.name || course?.hostName || 'Your teacher';
  const first = host.split(' ')[0];

  const [page, setPage] = useState(1);
  const [state, setState] = useState('idle');   // idle | loading | playing | paused | between | done | error
  const [note, setNote] = useState(null);
  const [answering, setAnswering] = useState(false);   // an answer is being spoken
  const [termsFor, setTermsFor] = useState(null);      // a question held until the chat terms are accepted
  const el = useRef(null);
  const ans = useRef(null);
  const run = useRef(0);          // bumps on every stop: late answers from an old run are dropped
  const asking = useRef(0);       // bumps on every question / close: a late reply is dropped
  const secs = useRef(0);
  const nextT = useRef(null);
  const ducked = useRef(false);
  const resumeAfterAsk = useRef(false);

  /* ── ANSWERS FROM THEIR VOICE AGENT (2026-11-04) ─────────────────────────
     Khari: "We should be using the voice agent here for answers, similar to how
     we have done in the rooms and other agent-mode answers" — after a typed
     question's answer came back as text and its recording (one clip for the
     whole answer, POST /api/learn/speak) was not ready in time: "AIGent's voice
     did not come through." So a typed question goes to the host's own agent,
     ONE-WAY (no mic: you type, they talk — VenueAgent's agent mode), briefed with
     the lesson (API LearnService.agentBrief): it answers out loud as it streams,
     its words on the board at once, saved to your thread with them, counted on
     the course's live voice. The call opens with the first question and closes
     a while after the last answer. The written way (/api/chat/message + the
     recording) is what is left when there is no agent: no voice plan, the
     course's live voice used up, or no answer in time. */
  const agentQ = useRef(null);      // { text, mine } typed while the call opens
  const agentFor = useRef(null);    // the question (asking.current) the agent is answering
  const agentOff = useRef(false);   // refused this visit: the written way from here
  const agentReplyT = useRef(null);
  const agentIdleT = useRef(null);
  const agent = useAvatarVoiceChat({
    avatarId: course?.hostId ?? null,
    surface: 'learn',
    learn: course && lesson ? { course: course.slug, lesson: lesson.slug, page: Math.max(1, Math.min(page, pages.length || 1)) } : null,
    oneWay: true,
    onReply: (text) => {
      const mine = agentFor.current;
      if (mine == null || mine !== asking.current || !text) return;
      clearTimeout(agentReplyT.current);
      const cur = useHypeU.getState().qa;
      setQa({ a: cur?.thinking || !cur?.a ? text : `${cur.a}\n\n${text}`, thinking: false, voiceNote: null });
      keepSeat(true);
    },
  });
  const agentRef = useRef(agent);
  agentRef.current = agent;
  /** Their voice stops (a closed question, back to the lesson, standing up); the call goes with it. */
  const hushAgent = () => {
    agentFor.current = null;
    agentQ.current = null;
    clearTimeout(agentReplyT.current);
    clearTimeout(agentIdleT.current);
    const a = agentRef.current;
    if (a.active || a.connecting) a.end();
  };

  useEffect(() => { if (course) loadCourses(); }, [course?.slug]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); setState('idle'); setNote(null); setQa(null); }, [course?.slug]);

  const talking = (on) => { if (useHypeU.getState().talking !== on) useHypeU.setState({ talking: on }); };
  const duck = (on) => { if (ducked.current !== on) { ducked.current = on; audioEngine.duck(on); } };

  const stopAnswer = () => {
    if (ans.current) { try { ans.current.pause(); } catch { /* gone */ } releaseAudio(ans.current); ans.current = null; }
    setAnswering(false);
  };
  const stop = () => {
    run.current += 1;
    clearTimeout(nextT.current);
    if (el.current) { try { el.current.pause(); } catch { /* gone */ } releaseAudio(el.current); el.current = null; }
    stopAnswer();
    talking(false);
    duck(false);
  };

  const playPage = async (n) => {
    stop();
    const mine = run.current;
    const pg = pages[n - 1];
    if (!pg) { setState('done'); setPage(pages.length || 1); return; }
    setPage(n);
    setNote(null);
    setState('loading');
    duck(true);
    keepSeat();
    let res; let data;
    try { ({ res, data } = await api.learnListen(course.slug, lesson.slug, n)); } catch { res = null; data = {}; }
    if (mine !== run.current) return;   // stood up, skipped, or left meanwhile
    if (!res?.ok || !data?.url) {
      setState('error');
      setNote(res?.status === 409 ? (data?.error || 'This class opens once its teacher has written it.') : 'The recording did not load — try again in a moment.');
      return;
    }
    secs.current = Number(data.seconds) || 0;
    const a = takeAudio(data.url);
    el.current = a;
    // Through the pool's own listeners: they go when the element goes back (a
    // pooled player keeps no handler of ours for its next borrower).
    onAudio(a, 'playing', () => { if (mine === run.current) { setState('playing'); talking(true); } });
    onAudio(a, 'pause', () => { if (mine === run.current) talking(false); });
    try { await a.play(); } catch { if (mine === run.current) { setState('paused'); setNote('Tap ▶ to start the class.'); } }
  };

  const pauseReading = () => {
    const a = el.current;
    if (state === 'playing' && a) { a.pause(); setState('paused'); duck(false); return true; }
    if (state === 'loading' || state === 'between') { run.current += 1; clearTimeout(nextT.current); setState('paused'); duck(false); return true; }
    return false;
  };
  const resumeReading = () => {
    const a = el.current;
    if (a && state === 'paused') { duck(true); a.play().then(() => setState('playing')).catch(() => {}); return; }
    playPage(state === 'done' ? 1 : page);
  };

  // ── ANSWERS, IN THEIR VOICE ────────────────────────────────────────────────
  const playAnswer = (url) => {
    if (!url) return;
    stopAnswer();
    const a = takeAudio(url);
    ans.current = a;
    setAnswering(true);
    duck(true);
    const done = () => { if (ans.current === a) { talking(false); duck(false); setAnswering(false); keepSeat(true); } };
    onAudio(a, 'playing', () => { if (ans.current === a) talking(true); });
    onAudio(a, 'pause', done);
    onAudio(a, 'ended', done);
    a.play().catch(() => { done(); setQa({ voiceNote: 'Tap ▶ under the board if the answer did not play.' }); });
  };
  const speakLive = async (messageId, mine) => {
    const lv = useHypeU.getState().liveVoice;
    const limit = lv?.limit ?? null;
    const used = lv?.used?.[course.slug] || 0;
    if (limit != null && used >= limit) {
      setQa({ voiceNote: `You've used this course's ${Math.round(limit / 60)} minutes of live voice — the answer is on the board.` });
      return;
    }
    try {
      // 60 s, not 30 (2026-11-04): a 1,271-character answer's clip was ready at 33 s, after the wait, and counted anyway.
      const { r, d } = await postJson('/api/learn/speak', { course: course.slug, lesson: lesson.slug, messageId: messageId || null }, 60000);
      if (mine !== asking.current) return;
      if (d?.limitReached) {
        useHypeU.setState((s) => ({ liveVoice: { ...(s.liveVoice || {}), used: { ...(s.liveVoice?.used || {}), [course.slug]: limit || 300 } } }));
        setQa({ voiceNote: d.error || 'Live voice for this course is used up — the answer is on the board.' });
        return;
      }
      if (!r.ok || !d?.audioBase64) { setQa({ voiceNote: `${first}'s voice did not come through. The answer is on the board.` }); return; }
      if (d.used != null) useHypeU.setState((s) => ({ liveVoice: { ...(s.liveVoice || {}), used: { ...(s.liveVoice?.used || {}), [course.slug]: d.used } } }));
      playAnswer(`data:audio/mpeg;base64,${d.audioBase64}`);
    } catch {
      if (mine === asking.current) setQa({ voiceNote: `${first}'s voice did not come through. The answer is on the board.` });
    }
  };

  const guestNote = () => setQa({ q: null, a: null, thinking: false, note: `Asking ${first} comes with a free HypeTunes account — your questions and their answers stay in your thread with them.`, cta: { label: 'Join HypeTunes', href: PLANS } });

  const send = async (text, { accepted = false } = {}) => {
    const msg = String(text || '').trim();
    if (!msg || !course || !lesson || useHypeU.getState().qa?.thinking) return;
    if (!AUTH_TOKEN) { guestNote(); return; }
    if (terms.needed && !accepted) { setTermsFor(msg); return; }
    stopAnswer();
    const mine = ++asking.current;
    keepSeat(true);
    setQa({ open: true, q: msg, a: null, thinking: true, note: null, cta: null, voiceNote: null });
    if (agentUsable()) { askAgent(msg, mine); return; }
    await askTyped(msg, mine);
  };

  /** Their agent can answer: not refused this visit, and live voice left on this course. */
  const agentUsable = () => {
    if (agentOff.current || !course?.hostId) return false;
    const lv = useHypeU.getState().liveVoice;
    const limit = lv?.limit ?? null;
    return limit == null || (lv?.used?.[course.slug] || 0) < limit;
  };
  const askAgent = (msg, mine) => {
    agentFor.current = mine;
    clearTimeout(agentIdleT.current);
    clearTimeout(agentReplyT.current);
    agentReplyT.current = setTimeout(() => {
      // Nothing back from the agent: the written way, so the question is still answered.
      if (mine !== asking.current || !useHypeU.getState().qa?.thinking) return;
      agentFor.current = null;
      askTyped(msg, mine);
    }, AGENT_REPLY_MS);
    const a = agentRef.current;
    if (a.active && a.say(msg)) return;
    agentQ.current = { text: msg, mine };
    if (!a.connecting) a.listen();
  };

  /** The written way: the answer as text, then read aloud (the website's /learn asking). */
  const askTyped = async (msg, mine) => {
    try {
      const { d } = await postJson('/api/chat/message', {
        avatarId: course.hostId, message: msg, surface: 'learn',
        learn: { course: course.slug, lesson: lesson.slug, page: Math.min(page, pages.length || 1) },
      });
      if (mine !== asking.current) return;
      if (d?.reply) {
        setQa({ a: d.reply, thinking: false });
        keepSeat(true);
        speakLive(d.messageId, mine);
      } else if (d?.requiresUpgrade) {
        setQa({ thinking: false, note: d.reason || "You've used your free messages.", cta: { label: d.ctaText || 'See plans', href: PLANS } });
      } else if (d?.code === 'PLAN_REQUIRED') {
        setQa({ thinking: false, note: 'Asking the hosts comes with a HypeTunes plan, including the free one.', cta: { label: 'See plans', href: PLANS } });
      } else {
        setQa({ thinking: false, note: `${first} could not answer that one. Try again in a moment.` });
      }
    } catch (e) {
      if (mine === asking.current) setQa({ thinking: false, note: e?.name === 'AbortError' ? 'No answer came back — try again.' : 'Connection lost. Try again in a moment.' });
    }
  };

  /** A suggested question: its RECORDED answer when there is one (instant, in their voice), else asked like anything else. */
  const chip = async (i) => {
    const q = lesson?.questions?.[i];
    if (!q || useHypeU.getState().qa?.thinking) return;
    if (!AUTH_TOKEN) { guestNote(); return; }
    if (!lesson.recorded?.[i]) { send(q); return; }
    stopAnswer();
    const mine = ++asking.current;
    keepSeat(true);
    setQa({ open: true, q, a: null, thinking: true, note: null, cta: null, voiceNote: null });
    try {
      const { r, d } = await postJson('/api/learn/answer', { course: course.slug, lesson: lesson.slug, q: i }, 20000);
      if (mine !== asking.current) return;
      if (!r.ok || !d?.answer) throw new Error('not recorded');
      setQa({ a: d.answer, thinking: false });
      playAnswer(d.url || null);
    } catch {
      if (mine === asking.current) { setQa({ thinking: false }); send(q); }
    }
  };

  // The buttons the board (and the phone sheet) press.
  classCtl.back = () => { keepSeat(true); hushAgent(); setQa(null); playPage(Math.max(1, page - 1)); };
  classCtl.next = () => { keepSeat(true); hushAgent(); setQa(null); playPage(page + 1); };
  classCtl.toggle = () => {
    keepSeat(true);
    if (useHypeU.getState().qa?.open) { stopAnswer(); hushAgent(); talking(false); asking.current += 1; setQa(null); }
    if (state === 'playing') { pauseReading(); return; }
    resumeReading();
  };
  classCtl.openAsk = () => {
    keepSeat(true);
    resumeAfterAsk.current = pauseReading();
    setQa({ open: true, q: null, a: null, thinking: false, note: null, cta: null, voiceNote: null, chips: lesson?.questions || [] });
    if (!AUTH_TOKEN) guestNote();
  };
  classCtl.closeAsk = () => {
    asking.current += 1;
    stopAnswer();
    hushAgent();
    talking(false);
    duck(false);
    setQa(null);
    setTermsFor(null);
    if (resumeAfterAsk.current) { resumeAfterAsk.current = false; resumeReading(); }
  };
  classCtl.send = send;
  classCtl.chip = chip;
  classCtl.typing = () => keepSeat();

  // Seated: the class starts (or picks up); stood up or gone: it stops.
  useEffect(() => {
    if (!active) {
      stop();
      hushAgent();
      asking.current += 1;
      setQa(null);
      setTermsFor(null);
      if (state === 'playing' || state === 'loading' || state === 'between') setState('paused');
      return undefined;
    }
    if (!voice) return undefined;
    if (state !== 'done') playPage(page);
    return undefined;
  }, [active, !!voice]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { stop(); useHypeU.setState({ lesson: null, qa: null }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // THEIR AGENT: the call is open — the question typed while it opened goes to it.
  useEffect(() => {
    if (!agent.active) return;
    const q = agentQ.current;
    agentQ.current = null;
    if (q && q.mine === asking.current && !agent.say(q.text)) { agentFor.current = null; askTyped(q.text, q.mine); }
  }, [agent.active]); // eslint-disable-line react-hooks/exhaustive-deps
  // Refused (no voice plan, no live voice left, the host away) or dropped: the written way for this visit.
  useEffect(() => {
    if (!agent.error) return;
    agentOff.current = true;
    console.info(`[Class] ${first}'s agent is not answering here (${agent.error}); answers come the written way`);
    if (/live voice/i.test(agent.error) && course) {
      useHypeU.setState((st) => ({ liveVoice: { ...(st.liveVoice || {}), used: { ...(st.liveVoice?.used || {}), [course.slug]: st.liveVoice?.limit || 300 } } }));
    }
    const q = agentQ.current;
    agentQ.current = null;
    if (q && q.mine === asking.current) { agentFor.current = null; clearTimeout(agentReplyT.current); askTyped(q.text, q.mine); }
  }, [agent.error]); // eslint-disable-line react-hooks/exhaustive-deps
  // While they are saying it: their face talks, the music dips, the seat is kept; then the call waits a while for the next one.
  useEffect(() => {
    if (agentFor.current == null) return undefined;
    talking(!!agent.speaking);
    duck(!!agent.speaking);
    setAnswering(!!agent.speaking);
    clearTimeout(agentIdleT.current);
    if (agent.speaking) { keepSeat(true); return undefined; }
    agentIdleT.current = setTimeout(() => { const a = agentRef.current; if (!a.speaking && (a.active || a.connecting)) a.end(); }, AGENT_IDLE_MS);
    return undefined;
  }, [agent.speaking]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { clearTimeout(agentReplyT.current); clearTimeout(agentIdleT.current); }, []);

  // A page is over at its measured length (or `ended`): a breath, then the next.
  useEffect(() => {
    if (state !== 'playing') return undefined;
    const iv = setInterval(() => {
      const a = el.current;
      if (!a) return;
      const over = a.ended || (secs.current > 0 && a.currentTime >= secs.current - 0.15);
      if (!over) return;
      clearInterval(iv);
      talking(false);
      setState('between');
      const mine = run.current;
      nextT.current = setTimeout(() => { if (mine === run.current) playPage(page + 1); }, BREATH_MS);
    }, 250);
    return () => clearInterval(iv);
  }, [state, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // BUSY KEEPS THE CHAIR: the reading running, an answer coming or being spoken.
  const busy = active && (state === 'playing' || state === 'loading' || state === 'between' || answering || !!qa?.thinking);
  useEffect(() => {
    if (!busy) return undefined;
    keepSeat();
    const iv = setInterval(() => keepSeat(), 20000);
    return () => clearInterval(iv);
  }, [busy]);

  // What the board draws.
  useEffect(() => {
    if (!course) { useHypeU.setState({ lesson: null }); return; }
    const pg = pages[page - 1] || null;
    useHypeU.setState({
      lesson: {
        page, total: pages.length, note, host,
        state: !courses ? 'finding' : !voice ? 'unwritten' : state,
        pageTitle: pg?.title || null,
        intro: page === 1 ? (voice?.intro || null) : null,
        body: pg ? (Array.isArray(pg.body) ? pg.body : [pg.body]).filter(Boolean) : [],
      },
    });
  }, [course?.slug, page, state, note, !!courses, voice]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!course) return null;

  const termsCard = termsFor && (
    <div style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 80 }}>
      <ChatAcceptance
        checking={terms.checking}
        onAccept={() => { terms.accept(); const q = termsFor; setTermsFor(null); send(q, { accepted: true }); }}
        onClose={() => setTermsFor(null)}
      />
    </div>
  );

  // In the room, not seated: say how it starts.
  if (!seatedHere) {
    return (
      <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', top: 'max(70px, 9vh)', zIndex: 60, pointerEvents: 'none', background: 'rgba(18,26,51,0.88)', border: `1px solid ${GOLD}66`, borderRadius: 999, padding: '7px 16px', color: '#f7f3ea', font: '600 13px system-ui' }}>
        <span style={{ color: GOLD }}>{course.title}</span> · take a seat and {first} will begin
      </div>
    );
  }

  // Wide: the board carries everything (Classroom). Only the terms, if asked.
  if (!narrow) return termsCard || null;

  // A PHONE: the board keeps the titles; the page (or the question) reads here.
  const pg = pages[page - 1] || null;
  const qaOn = qa?.open && (qa.q || qa.note);
  return (
    <>
      {termsCard}
      <div
        style={{ position: 'fixed', left: 10, right: 10, bottom: 'calc(max(12px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))', maxHeight: '48dvh', zIndex: 66, display: 'flex', flexDirection: 'column', background: 'rgba(18,26,51,0.94)', border: `1px solid ${GOLD}55`, borderRadius: 16, color: '#f7f3ea', fontFamily: 'system-ui, sans-serif', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', pointerEvents: 'auto', overflow: 'hidden' }}
        role="region"
        aria-label={`${course.title} class`}
      >
        <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(216,166,64,0.2)' }}>
          <div style={{ font: '700 10px system-ui', letterSpacing: 2.2, color: GOLD, textTransform: 'uppercase' }}>Hype-U · with {host}</div>
          <div style={{ font: '700 16px Georgia, serif', marginTop: 3 }}>{lesson?.title || course.title}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', fontSize: 14, lineHeight: 1.5 }}>
          {!courses && <div style={{ opacity: 0.7 }}>Finding the lesson…</div>}
          {courses && !voice && <div style={{ opacity: 0.8 }}>{first} hasn&apos;t written this class yet — check back soon.</div>}
          {qaOn ? (
            <>
              {qa.q && <div style={{ color: GOLD, fontWeight: 700, marginBottom: 6 }}>“{qa.q}”</div>}
              {qa.thinking && <div style={{ opacity: 0.75 }}>{first} is thinking…</div>}
              {qa.a && String(qa.a).split(/\n{2,}/).map((p, i) => <p key={i} style={{ margin: '0 0 8px' }}>{p}</p>)}
              {qa.note && <div style={{ opacity: 0.85 }}>{qa.note}</div>}
            </>
          ) : (
            <>
              {state === 'done' && <div style={{ color: GOLD, fontWeight: 700, marginBottom: 8 }}>Class dismissed. Questions? Ask {first} below.</div>}
              {pg && state !== 'done' && (
                <>
                  {page === 1 && voice?.intro && <p style={{ margin: '0 0 8px', opacity: 0.85, fontStyle: 'italic' }}>{voice.intro}</p>}
                  <div style={{ font: '700 14.5px system-ui', color: GOLD, marginBottom: 5 }}>{pg.title}</div>
                  {(Array.isArray(pg.body) ? pg.body : [pg.body]).filter(Boolean).map((para, i) => <p key={i} style={{ margin: '0 0 8px' }}>{para}</p>)}
                </>
              )}
            </>
          )}
        </div>
        <div style={{ padding: '8px 10px 10px', borderTop: '1px solid rgba(216,166,64,0.2)' }}>
          <ClassControls host={host} />
        </div>
      </div>
    </>
  );
}
